import type { ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { Loader2, ShieldAlert } from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAuth } from '@/features/auth/useAuth'

/**
 * Route guard. Three outcomes: still loading, not signed in (→ login), signed in but not an admin
 * (→ explanation + sign out). Everything behind it may assume an admin profile.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const { isLoading, user, profile, isAdmin, error, signOut } = useAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-svh items-center justify-center">
        <Loader2 className="text-muted-foreground size-6 animate-spin" />
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />
  }

  if (!isAdmin) {
    return (
      <main className="flex min-h-svh items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardHeader className="items-center text-center">
            <div className="bg-destructive/10 text-destructive mx-auto flex size-14 items-center justify-center rounded-2xl">
              <ShieldAlert className="size-7" />
            </div>
            <CardTitle>Доступ запрещён</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <p className="text-muted-foreground text-sm">
              Вы вошли как <span className="text-foreground font-medium">{user.email}</span>, но у
              этого аккаунта нет роли администратора
              {profile ? '' : ' (профиль в Firestore не найден)'}.
            </p>
            {error ? (
              <Alert variant="destructive">
                <AlertTitle>Профиль не прочитан</AlertTitle>
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            ) : null}
            <p className="text-muted-foreground text-xs">
              Роль назначается в Firebase Console: Firestore → users → ваш документ → поле role =
              admin. Правила безопасности запрещают менять role из клиента.
            </p>
            <Button variant="outline" onClick={() => void signOut()}>
              Выйти
            </Button>
          </CardContent>
        </Card>
      </main>
    )
  }

  return <>{children}</>
}
